import React from 'react';

import {DraftMessageModel} from '../../../../../../libs/shared/core/src/models/api/messageModel';
import {TeammateModel} from '../../../../../../libs/shared/core/src/models/api/teammateModel';
import {ViewerPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {TimelineVariationsEnum} from '../../../../../../libs/shared/core/src/store/timelineVariations/timelineVariationsTypes';
import {computeIsDraftInReadMode} from '../../../../../../srcCommon/helpers/messageDraftHelpers';
import {makeConversationActionContext} from '../../../../../helpers/models/conversationHelpers';
import {isMessageVisuallyShared} from '../../../../../helpers/models/messageHelpers';
import {ActivityModel} from '../../../../../models/api/activityModel';
import {ConversationModel} from '../../../../../models/api/conversationModel';
import {ViewerPanelConversationPageState} from '../../../../../store/panels/viewer/viewerPanelState';
import {MessageComposerContainer} from '../../messageComposer/messageComposerContainer';
import {MessageViewerContainer} from './messageViewerContainer';

/*
 * Types.
 */

export interface MessageDraftsProps {
  panelId: ViewerPanelId;
  userId: number;
  page: ViewerPanelConversationPageState;
  conversation: ConversationModel;
  activities: ReadonlyArray<ActivityModel>;
  replyingTeammates: ReadonlyArray<TeammateModel>;
  readonlyDraftMessageUids: ReadonlySet<string>;
  isSpam: boolean;
  composeDraftMessages: ReadonlyArray<DraftMessageModel>;
  timelineVariation: TimelineVariationsEnum;
  canComposerPopout: boolean;
}

/*
 * Helpers.
 */

// This is a helper function instead of a component, because we want drafts to be direct children of
// VariableHeightScroller. If we nest them inside of another component, we cannot scroll to a specific draft.
export function renderMessageDrafts(props: MessageDraftsProps): Array<React.ReactElement> {
  const {panelId, userId, conversation, isSpam, page, timelineVariation} = props;

  return props.composeDraftMessages.map((draft) => {
    // Shared draft from other teammate OR readonly draft.
    const isDraftShared = isMessageVisuallyShared(draft, conversation, userId);
    const isDraftInReadMode = computeIsDraftInReadMode(
      draft,
      userId,
      props.readonlyDraftMessageUids,
      isDraftShared,
    );
    if (isDraftInReadMode) {
      return (
        <MessageViewerContainer
          key={draft.uid}
          panelId={panelId}
          messageUid={draft.uid}
          cellsContext={page.cellsContext}
          namespace={conversation.namespace}
          conversationId={conversation.id}
          conversationActionContext={makeConversationActionContext(page.cellsContext, conversation)}
          isSpam={isSpam}
          isAuthorReplying={props.replyingTeammates.some((r) => r.id === draft.authorId)}
          isSharedDraft
          isFirstMessage={props.activities.length === 0}
          timelineVariation={timelineVariation}
        />
      );
    }

    // Composer draft.
    return (
      <MessageComposerContainer
        key={draft.uid}
        panelId={panelId}
        message={draft}
        cellsContext={page.cellsContext}
        parameters={page.parameters}
        shouldFocus={page.parameters.focusDraftUid === draft.uid}
        conversation={conversation}
        isFirst={props.activities.length === 0}
        timelineVariation={timelineVariation}
        canPopout={props.canComposerPopout}
        isOnlyComposer={props.composeDraftMessages.length === 1}
      />
    );
  });
}
